import { InputCheckbox } from '@creditornot/cb-checkbox';
import { CaretRight } from '@creditornot/cb-icons';
import { cbSemanticCssVars, colors } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React, { useState } from 'react';
import styled from 'styled-components';

import { typographyCss } from 'common/typography';

type CategoryAccordionItemProps = {
  id: string;
  title: string;
  checked?: boolean;
  partiallyChecked?: boolean;
  onCheckboxClick?: (selectAll: boolean) => void;
  children: React.ReactNode;
};

const AccordionSection = styled.div``;

const AccordionHeader = styled.div`
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const CaretContainer = styled.div`
  flex-shrink: 0;
  border-radius: 12px;
  height: 24px;
  width: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  /* color-bg-surface */
  background: ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
`;

const CaretIcon = styled(CaretRight)`
  height: 12px;
  width: 12px;
  fill: ${cbSemanticCssVars.textPrimary};
  transition: transform 500ms cubic-bezier(0.16, 1, 0.3, 1);

  &[data-direction='down'] {
    transform: rotate(90deg);
  }

  [dir='rtl'] & {
    transform: rotate(180deg);

    &[data-direction='down'] {
      transform: rotate(90deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0ms;
  }
`;

const AccordionButton = styled.button`
  background: transparent;
  padding-block: 16px;
  padding-inline: 24px 16px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${cbSemanticCssVars.textPrimary};
  border: none;
  overflow: hidden;
  cursor: pointer;
`;

const CategoryTitle = styled.div`
  ${typographyCss.Heading6}
  margin-inline-end: auto;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Collapse = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  transition:
    grid-template-rows 500ms cubic-bezier(0.16, 1, 0.3, 1),
    opacity 500ms cubic-bezier(0.16, 1, 0.3, 1);

  &[data-hidden='false'] {
    grid-template-rows: 1fr;
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0ms;
  }
`;

const CollapsedContent = styled.div`
  overflow: hidden;
`;

export const CategoryAccordionItem = ({
  id,
  title,
  checked,
  partiallyChecked,
  onCheckboxClick,
  children,
}: CategoryAccordionItemProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [renderCollapsedContent, setRenderCollapsedContent] = useState(isExpanded);

  const buttonId = `button-${id}`;
  const contentId = `content-${id}`;

  React.useEffect(() => {
    if (isExpanded && !renderCollapsedContent) {
      setRenderCollapsedContent(true);
    }
  }, [isExpanded, renderCollapsedContent]);

  return (
    <AccordionSection>
      <AccordionHeader>
        <AccordionButton
          type="button"
          aria-controls={contentId}
          aria-expanded={isExpanded}
          id={buttonId}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {!!onCheckboxClick && (
            <InputCheckbox
              checked={checked}
              partiallyChecked={partiallyChecked}
              onClick={event => event.stopPropagation()}
              onChange={() => {
                onCheckboxClick(!checked && !partiallyChecked);
              }}
            />
          )}

          <CategoryTitle>{title}</CategoryTitle>

          <CaretContainer>
            <CaretIcon data-direction={isExpanded ? 'down' : 'right'} />
          </CaretContainer>
        </AccordionButton>
      </AccordionHeader>
      <Collapse
        data-hidden={!isExpanded}
        onTransitionEnd={() => {
          if (!isExpanded) {
            setRenderCollapsedContent(false);
          }
        }}
      >
        {renderCollapsedContent && (
          <CollapsedContent aria-labelledby={buttonId} id={contentId}>
            {children}
          </CollapsedContent>
        )}
      </Collapse>
    </AccordionSection>
  );
};
